import { useState } from 'react';

import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Archive } from 'lucide-react';

import { ConfirmationModal } from './Confirmation';
import Modal, { ModalProps } from './CustomModal';
import { useSelectedFactsDb } from '../../../screens/Chronos/CaseEditor/DataView/FactsEditor/hooks/useSelectedFactsDb';
import { RelevanceDropdown } from '../../organisms/RelevanceDropdown';
import { useBulkDeleteFacts } from '@/api/mutations/useBulkDeleteFacts';
import { useBulkUpdateFact } from '@/api/mutations/useBulkUpdateFact';
import { useSetKeyForFacts } from '@/api/mutations/useSetKeyForFacts';
import { Button } from '@/components/atoms/new-button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/atoms/ShadCnSelect';
import { Relevance } from '@/types';

interface FactBulkUpdateModalProps extends Omit<ModalProps, 'content'> {
  caseId: string;
  selectedFactCount: number;
  onSuccess?: () => void;
}

export const FactBulkUpdateModal = ({ caseId, selectedFactCount, onSuccess, ...props }: FactBulkUpdateModalProps) => {
  const [selectedRelevance, setSelectedRelevance] = useState<Relevance>(Relevance.YES);
  const [selectedKeyStatus, setSelectedKeyStatus] = useState<'key' | 'not-key'>('key');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { getAllSelectedIds, deselectMultiple } = useSelectedFactsDb();

  const { mutate: updateFacts, isLoading } = useBulkUpdateFact(caseId);
  const { mutate: updateKeyStatus, isLoading: isUpdatingKeyStatus } = useSetKeyForFacts(caseId);
  const { mutate: deleteFacts, isLoading: isDeletingFacts } = useBulkDeleteFacts(caseId);

  const handleConfirm = async () => {
    const factIds = await getAllSelectedIds(caseId);
    updateFacts(
      { relevance: selectedRelevance, factIds },
      {
        onSuccess: () => {
          onSuccess?.();
          props.handleClose();
        },
      },
    );
  };

  const handleKeyStatusUpdate = async () => {
    const factIds = await getAllSelectedIds(caseId);
    updateKeyStatus(
      { key: selectedKeyStatus === 'key', factIds },
      {
        onSuccess: () => {
          onSuccess?.();
          props.handleClose();
        },
      },
    );
  };

  const handleDeleteAll = async () => {
    const factIds = await getAllSelectedIds(caseId);
    deleteFacts(
      { factIds },
      {
        onSuccess: () => {
          onSuccess?.();
          props.handleClose();
          setIsDeleteModalOpen(false);
          deselectMultiple(caseId, factIds);
        },
      },
    );
  };

  return (
    <>
      <Modal
        {...props}
        content={
          <div className="flex flex-col">
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Bulk Update Facts</h3>
              <p className="mt-1 text-sm text-gray-600">
                Update {selectedFactCount} selected fact{selectedFactCount === 1 ? '' : 's'}
              </p>
            </div>

            <div className="px-6 py-4">
              <div className="mb-6">
                {isLoading ? (
                  <div className="flex justify-center p-4 text-gray-500">
                    <FontAwesomeIcon icon={faCircleNotch} className="h-6 w-6 animate-spin" />
                  </div>
                ) : (
                  <div className="to-blue-25 rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 p-5">
                    <div className="flex items-end justify-between gap-2">
                      <div className="w-56">
                        <RelevanceDropdown
                          title=""
                          value={selectedRelevance}
                          updateRelevance={setSelectedRelevance}
                          isLoading={isLoading}
                        />
                      </div>
                      <Button
                        data-testid="confirm-bulk-update-button"
                        variant="primary"
                        onClick={handleConfirm}
                        disabled={isLoading || isDeletingFacts || isUpdatingKeyStatus}
                        className="shrink-0"
                      >
                        {isLoading
                          ? 'Updating...'
                          : `Update ${selectedFactCount} Fact${selectedFactCount === 1 ? '' : 's'}`}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Key Facts Update */}
              <div className="mb-6">
                {isUpdatingKeyStatus ? (
                  <div className="flex justify-center p-4 text-gray-500">
                    <FontAwesomeIcon icon={faCircleNotch} className="h-6 w-6 animate-spin" />
                  </div>
                ) : (
                  <div className="to-yellow-25 rounded-lg border border-yellow-300 bg-gradient-to-r from-yellow-100 p-5">
                    <div className="flex items-end justify-between gap-2">
                      <div className="w-56">
                        <label className="mb-2 block font-semibold text-gray-800">Key</label>
                        <Select
                          value={selectedKeyStatus}
                          onValueChange={(value: 'key' | 'not-key') => setSelectedKeyStatus(value)}
                        >
                          <SelectTrigger className="bg-white shadow-none">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="key">Mark as Key</SelectItem>
                            <SelectItem value="not-key">Remove Key Status</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        variant="primary"
                        onClick={handleKeyStatusUpdate}
                        disabled={isLoading || isDeletingFacts || isUpdatingKeyStatus}
                        className="shrink-0"
                      >
                        {isUpdatingKeyStatus
                          ? 'Updating...'
                          : `Update ${selectedFactCount} Fact${selectedFactCount === 1 ? '' : 's'}`}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Danger Zone */}
              <div className="border-t border-gray-200 pt-6">
                <div className="to-red-25 rounded-lg border border-red-200 bg-gradient-to-r from-red-50 p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Archive className="text-red-600" size={16} />
                      <div>
                        <p className="text-sm text-red-600">
                          Archive {selectedFactCount} fact{selectedFactCount === 1 ? '' : 's'} from this case
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setIsDeleteModalOpen(true)}
                      disabled={isLoading || isDeletingFacts || isUpdatingKeyStatus || selectedFactCount === 0}
                      className="shrink-0"
                    >
                      Archive ({selectedFactCount})
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        handleClose={() => setIsDeleteModalOpen(false)}
        title="Archive Selected Facts"
        description={
          <div>
            <p className="mb-3 text-sm text-gray-700">
              Are you sure you want to archive the <strong>{selectedFactCount}</strong> selected fact
              {selectedFactCount === 1 ? '' : 's'}?
            </p>

            <p className="text-xs text-gray-500">
              Currently you cannot view or un-archive archived facts. This will be added in a future update.
            </p>
          </div>
        }
        onConfirm={handleDeleteAll}
        isLoading={isDeletingFacts}
        confirmButtonText={`Archive Facts (${selectedFactCount})`}
        confirmButtonVariant="destructive"
      />
    </>
  );
};
